import React from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { withTracker } from 'meteor/react-meteor-data';
import { isEmpty, filter, map, find, isEqual, includes, cloneDeep } from 'lodash';
import { Bert } from 'meteor/themeteorchef:bert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Stack, Typography, Autocomplete, FormControlLabel, Checkbox } from '@mui/material';
import {
  renderTextField,
  renderSelect,
  renderButton,
  renderSwitch,
  renderIconButton
} from '../../components/MaterialMUIComponent';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

import Connectors, {
  CONNECTOR_SUBTYPES,
  CONNECTOR_TYPES
} from '../../../api/Connectors/Connectors';
import Sources from '../../../api/Sources/Sources';

import PageHeader from '../../atoms/PageHeader';
import NotFound from '../NotFound';
import Loading from '../../components/Loading';
import * as sourceOps from '../../../api/Sources/source-operations';
import * as connectorOps from '../../../api/Connectors/connector-operations';

import TableSourceComponent from '../../components/TableSourceComponent';

const TYPES_CONNECTIONS = ['FTP', 'GeoJSON VT', 'HTTP', 'PostgreSQL', 'WMS'];
const STATUS_OK = 'ok';
const LIMIT_DATA_DEFAULT = 50;

const NewSource = ({ history, loading, connectors, source, isEditorMode }) => {
  if (loading) return <Loading />;
  if (sourceOps.isDeleted(source) && !isEmpty(source)) return <NotFound />;
  const [newSourceSchema, setNewSourceSchema] = React.useState({
    name: sourceOps.getName(source),
    connector:
      !isEmpty(connectors) && !isEmpty(source)
        ? find(connectors, conn => connectorOps.getId(conn) === sourceOps.getConnectorId(source))
        : {},
    sourceSelected: sourceOps.getLayerName(source),
    attributesSource: sourceOps.getAttributes(source)
  });
  const [filterConnector, setFilterConnector] = React.useState(TYPES_CONNECTIONS);

  const [loadingSources, setLoadingSources] = React.useState(false);
  const [sources, setSources] = React.useState([]);
  const [attributes, setAttributes] = React.useState([]);
  const [isSQLQuery, setIsSQLQuery] = React.useState(false);
  const [textFilterSQL, setTextFilterSQL] = React.useState({
    text: sourceOps.getSQLQuery(source),
    sqlFields: Array.toString(sourceOps.getAttributes(source))
  });
  const [alertSQLQuery, setAlertSQLQuery] = React.useState({
    error: false,
    text: 'SQL sentences not correct'
  });

  const [isPreviewSource, setIsPreviewSource] = React.useState(false);
  const [viewTable, setViewTable] = React.useState(false);
  const [limitConsultingData, setLimitConsultingData] = React.useState('All');

  const [existingWrapper, setExistingWrapper] = React.useState(false);
  const [WrapperValue, setWrapperValue] = React.useState(false);
  const [Manual, setManual] = React.useState([sourceOps.getLayerNameSource(source)]);
  const [generateWrapper, setGenerateWrapper] = React.useState(false);

  const isOutputConnector = connectorOps.isOutput(newSourceSchema.connector);
  const isManualLayer =
    isEmpty(Manual) &&
    !isEmpty(connectorOps.getId(newSourceSchema.connector)) &&
    isEqual(connectorOps.getType(newSourceSchema.connector), CONNECTOR_TYPES.HTTP);
  const showWrapperOptions =
    !isOutputConnector &&
    includes(
      [CONNECTOR_SUBTYPES.VECTOR, CONNECTOR_SUBTYPES.THEMATIC],
      connectorOps.getSubType(newSourceSchema.connector)
    );

  const detectSQLInjection = sqlText => {
    const wordsKey = [
      'INSERT',
      'UPDATE',
      'DELETE',
      'DROP',
      'CREATE',
      'ALTER',
      'TABLE',
      'UNION',
      'TRUNCATE',
      'INTO'
    ];
    let isSQLInjection = false;
    map(wordsKey, word => {
      if (sqlText.includes(word)) isSQLInjection = true;
    });
    if (isSQLInjection) setAlertSQLQuery({ ...alertSQLQuery, error: true });
    return isSQLInjection;
  };

  const generateTextFieldSQLQuery = arrayAttr => {
    let newTextFields = '';
    map(arrayAttr, (item, index, array) => {
      newTextFields = `${newTextFields}"${item}"`;
      if (!isEqual(index, array.length - 1)) newTextFields = `${newTextFields},`;
    });
    return newTextFields;
  };
  const generateTextSQLQuery = (textFields, nameSource = newSourceSchema.sourceSelected) =>
    `SELECT ${textFields} FROM ${newSourceSchema.connector.schema}.${nameSource}`;

  const generateStartSQLQuery = (attrArray, nameSource = newSourceSchema.sourceSelected) => {
    const textSQL = generateTextSQLQuery(generateTextFieldSQLQuery(attrArray), nameSource);
    setTextFilterSQL({
      text: textSQL,
      sqlFields: generateTextFieldSQLQuery(attrArray)
    });
    setAlertSQLQuery({ ...alertSQLQuery, error: false });
  };

  const handleConnector = async nameConnector => {
    setLoadingSources(true);
    const connector = find(connectors, conn => isEqual(connectorOps.getName(conn), nameConnector));
    if (!isEmpty(connector)) {
      try {
        const sourcesConnector = await methodPromise('connectors.listSources', connector, false);
        const newSchema = {
          ...newSourceSchema,
          connector,
          sourceSelected: '',
          attributesSource: []
        };
        if (
          isEditorMode &&
          !isEmpty(source) &&
          isEqual(sourceOps.getConnectorId(source), connectorOps.getId(connector))
        ) {
          newSchema.sourceSelected = sourceOps.getLayerName(source);
          newSchema.attributesSource = sourceOps.getAttributes(source);
        }
        setNewSourceSchema(newSchema);
        setSources(sourcesConnector);
        setManual(sourcesConnector);
        setIsSQLQuery(false);
      } catch (error) {
        Bert.alert('Error searching list sources connector', 'danger');
        handleErrorInClient(error);
      }
    }
    setLoadingSources(false);
  };

  const handleSource = async nameSource => {
    try {
      const sourceObject = {
        connectorId: connectorOps.getId(newSourceSchema.connector),
        layerNameSource: nameSource,
        layerName: nameSource
      };
      const newAttributes = await methodPromise('sources.listSourceAttributes', sourceObject);
      setNewSourceSchema({
        ...newSourceSchema,
        sourceSelected: nameSource,
        attributesSource: newAttributes
      });
      setAttributes(newAttributes);
      generateStartSQLQuery(newAttributes, nameSource);
      setIsPreviewSource(false);
    } catch (error) {
      Bert.alert('Error searching attributes sources', 'danger');
      handleErrorInClient(error);
    }
  };

  const hanldeAttributes = (newArray, method, selection) => {
    if (isSQLQuery) {
      const { option } = selection;
      let newTextFilter = cloneDeep(textFilterSQL.sqlFields);
      const lengthArrayAttr = newSourceSchema.attributesSource.length;
      const indexOption = newSourceSchema.attributesSource.indexOf(option);
      if (isEqual(method, 'removeOption') && !isEqual(indexOption, lengthArrayAttr - 1))
        newTextFilter = textFilterSQL.sqlFields.replace(`"${option}",`, '');
      else newTextFilter = textFilterSQL.sqlFields.replace(`,"${option}"`, '');
      if (isEqual(method, 'selectOption')) newTextFilter = `${textFilterSQL.sqlFields},"${option}"`;
      setTextFilterSQL({
        text: generateTextSQLQuery(newTextFilter),
        sqlFields: newTextFilter
      });
    }
    const valueSave = !isEmpty(newArray) ? newArray : attributes;
    setNewSourceSchema({ ...newSourceSchema, attributesSource: valueSave });
    setViewTable(false);
  };

  const handleSQLQueryText = objectSQL => {
    const newText = cloneDeep(objectSQL.text);
    const nameSchemaTable = `${newSourceSchema.connector.schema}.${newSourceSchema.sourceSelected}`;
    if (
      !newText.includes('SELECT') ||
      !newText.includes('FROM') ||
      !newText.includes(nameSchemaTable)
    ) {
      setTextFilterSQL({ ...textFilterSQL, text: generateTextSQLQuery(textFilterSQL.sqlFields) });
    } else {
      const restText = newText.substring(newText.indexOf(nameSchemaTable) + nameSchemaTable.length);
      const newTextFieldsSQL = newText.substring(
        newText.indexOf('SELECT') + 'SELECT'.length,
        newText.indexOf('FROM')
      );
      const arrayAttrTextFields = map(newTextFieldsSQL.split(','), word => {
        const newWord = word.replaceAll('"', '');
        return newWord.trim();
      });
      const arrayNoDuplicate = Array.from(new Set(arrayAttrTextFields));
      const checkNewAttributes = filter(arrayNoDuplicate, item => {
        return !isEmpty(item) && attributes.includes(item);
      });
      const newAttributes = !isEmpty(checkNewAttributes) ? checkNewAttributes : attributes;
      const isInjectionSQL = detectSQLInjection(restText);
      if (!isInjectionSQL) {
        setNewSourceSchema({ ...newSourceSchema, attributesSource: newAttributes });
        setTextFilterSQL({
          text: generateTextSQLQuery(generateTextFieldSQLQuery(newAttributes)) + restText,
          sqlFields: generateTextFieldSQLQuery(newAttributes)
        });
        setAlertSQLQuery({ ...alertSQLQuery, error: false });
      }
    }
  };

  const redirectToSourceView = (existingSource, sourceId) => {
    const confirmation = existingSource ? 'Source updated!' : 'Source added!';
    Bert.alert(confirmation, 'success');
    history.push(`/sources/${sourceId}`);
  };

  const handleSubmit = async () => {
    try {
      const methodToCall = !isEmpty(source) ? 'sources.update' : 'sources.insert';
      const isOutput = connectorOps.isOutput(newSourceSchema.connector);
      const isRaster = newSourceSchema.connector.subtype === 'RASTER';
      const isGeneric = newSourceSchema.connector.subtype === 'GENERIC';
      const newSource = {
        name: newSourceSchema.name,
        connectorId: connectorOps.getId(newSourceSchema.connector),
        layerNameSource: isOutput ? '' : newSourceSchema.sourceSelected,
        layerName: newSourceSchema.sourceSelected,
        attributes: isOutput ? [] : newSourceSchema.attributesSource,
        sqlQuery: textFilterSQL.text
      };

      let generate = !isOutput && !existingWrapper && !isRaster && !isGeneric;
      let overwrite = !isOutput && existingWrapper;
      if (
        !isOutput &&
        includes(
          [CONNECTOR_SUBTYPES.VECTOR, CONNECTOR_SUBTYPES.THEMATIC],
          connectorOps.getSubType(newSourceSchema.connector)
        )
      ) {
        generate = !existingWrapper && generateWrapper;
        overwrite = WrapperValue || generate;
      }

      if (!isEmpty(source)) newSource._id = sourceOps.getId(source);

      const sourceId = await methodPromise(methodToCall, newSource, { generate, overwrite });
      redirectToSourceView(!isEmpty(source), sourceId);
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  const checkIfExistingWrapper = async () => {
    let newExistingWrapper = false;
    try {
      if (!isEmpty(sourceOps.getLayerName(source)));
      newExistingWrapper = await methodPromise('sources.checkIfExistingWrapper', source);
    } catch (error) {
      handleErrorInClient(error);
    }
    setExistingWrapper(newExistingWrapper);
    setWrapperValue(newExistingWrapper);
  };

  const checkSource = async () => {
    try {
      const sourceObject = {
        connectorId: connectorOps.getId(newSourceSchema.connector),
        layerNameSource: newSourceSchema.sourceSelected,
        layerName: newSourceSchema.sourceSelected
      };
      if (!isEmpty(source)) checkIfExistingWrapper();
      const { status, message } = await methodPromise('sources.checkIfValid', sourceObject);
      if (status === STATUS_OK) {
        setIsPreviewSource(true);
        Bert.alert(message, 'success');
      } else {
        throw new Error(message);
      }
    } catch (error) {
      Bert.alert('Source is not valid', 'danger');
      handleErrorInClient(error);
    }
  };

  const buttons = [
    {
      text: !isEditorMode ? 'Add source' : 'Update source',
      color: 'success',
      key: 'save-source',
      disabled:
        isEmpty(newSourceSchema.sourceSelected) ||
        isEmpty(newSourceSchema.name) ||
        isEmpty(newSourceSchema.connector),
      onclick: () => handleSubmit(),
      isShow: true,
      style: {}
    },
    {
      text: !viewTable ? 'Preview source' : 'Hidden source',
      color: 'primary',
      key: 'preview-source',
      disabled: connectorOps.isOutput(newSourceSchema.connector) || !isPreviewSource,
      onclick: () => setViewTable(!viewTable),
      isShow: true,
      style: {
        color:
          connectorOps.isOutput(newSourceSchema.connector) || !isPreviewSource ? 'grey' : '#2A3F90',
        borderColor:
          connectorOps.isOutput(newSourceSchema.connector) || !isPreviewSource ? 'grey' : '#2A3F90'
      }
    },
    {
      text: 'Check source',
      color: 'primary',
      key: 'check-source',
      disabled: isEmpty(newSourceSchema.sourceSelected),
      onclick: () => checkSource(),
      isShow: true,
      style: {
        color: isEmpty(newSourceSchema.sourceSelected) ? 'grey' : '#2A3F90',
        borderColor: isEmpty(newSourceSchema.sourceSelected) ? 'grey' : '#2A3F90'
      }
    }
  ];

  const renderManyButton = array =>
    map(
      array,
      item =>
        item.isShow &&
        renderButton({
          key: `button-${item.key}`,
          onClick: item.onclick,
          contentButton: item.text,
          disabled: item.disabled,
          color: item.color,
          style: item.style
        })
    );

  React.useEffect(() => {
    if (!isEmpty(source) && !isEmpty(connectors)) {
      const connector = find(connectors, conn =>
        isEqual(connectorOps.getId(conn), sourceOps.getConnectorId(source))
      );
      if (!isEmpty(connector)) handleConnector(connectorOps.getName(connector));
      handleSource(sourceOps.getLayerName(source));
    }
  }, []);
  return (
    <>
      <PageHeader>
        <h4>{!isEditorMode ? 'New Source' : `Editing "${sourceOps.getName(source)}`}</h4>
      </PageHeader>
      <Stack direction="row" width="100%">
        <Stack direction="column" width="30%" marginRight="2%">
          <Typography>Name</Typography>
          {renderTextField({
            key: 'name-newSource',
            style: { width: '100%', marginBottom: '2%' },
            moreProps: {
              value: newSourceSchema.name,
              onChange: event =>
                setNewSourceSchema({ ...newSourceSchema, name: event.target.value }),
              placeholder: "Oh, The Places You'll Go!"
            }
          })}
          <Typography>Connection</Typography>
          <Autocomplete
            multiple
            size="small"
            options={TYPES_CONNECTIONS}
            getOptionLabel={option => option}
            onChange={(index, newValue) => {
              const valueSave = !isEmpty(newValue) ? newValue : TYPES_CONNECTIONS;
              setFilterConnector(valueSave);
              setViewTable(false);
            }}
            value={filterConnector}
            renderInput={params =>
              renderTextField({
                key: 'select-connection',
                moreProps: {
                  ...params,
                  placeholder: 'Select filters to connections'
                }
              })
            }
            style={{ marginBottom: '2%' }}
          />
          {renderSelect({
            key: `select-connection`,
            label: 'BBDD',
            inputLabel: 'BBDD',
            multiple: true,
            arrayOptions: map(
              filter(connectors, conn => filterConnector.includes(connectorOps.getType(conn))),
              item => item.name
            ),
            style: { marginBottom: '2%', maxHeight: '20vh' },
            onChange: event => handleConnector(event.target.value),
            value: !isEmpty(newSourceSchema.connector) ? [newSourceSchema.connector.name] : []
          })}
          {loadingSources && <Loading />}
          {!isEmpty(sources) && !isOutputConnector && (
            <>
              <Typography>
                {connectorOps.isPostgreSQL(newSourceSchema.connector)
                  ? 'Table View Name'
                  : 'Layer Name'}
              </Typography>

              <Autocomplete
                size="small"
                options={sources}
                value={newSourceSchema.sourceSelected}
                onChange={(index, newValue) => {
                  handleSource(newValue);
                  setViewTable(false);
                }}
                renderInput={params =>
                  renderTextField({
                    key: 'select-source',
                    moreProps: { ...params, placeholder: 'Select source/table' }
                  })
                }
                style={{ marginBottom: '2%' }}
              />
            </>
          )}
          {isManualLayer && (
            <>
              <Typography>Selecione la Layer manualmente</Typography>
              {renderTextField({
                key: 'ManualLayerName',
                moreProps: {
                  defaultValue: '',
                  onChange: event => setSources([event.target.value.trim()]),
                  placeholder: "Oh, The Places You'll Go!"
                }
              })}
            </>
          )}
          {!isEmpty(newSourceSchema.sourceSelected) && !isEmpty(attributes) && !isOutputConnector && (
            <>
              <Stack direction="row" display="flex">
                <Typography>Attributes</Typography>
                {renderIconButton({
                  key: 'Searching-sql',
                  onClick: () => {
                    if (isEqual(!isSQLQuery, true))
                      generateStartSQLQuery(newSourceSchema.attributesSource);
                    setIsSQLQuery(!isSQLQuery);
                  },
                  icon: <FontAwesomeIcon icon="database" />,
                  titleTooltip: 'Generate SQL Query'
                })}
              </Stack>
              <Autocomplete
                multiple
                size="small"
                options={attributes}
                getOptionLabel={option => option}
                onChange={(event, newArray, method, selection) =>
                  hanldeAttributes(newArray, method, selection)
                }
                value={
                  !isEmpty(newSourceSchema.attributesSource)
                    ? newSourceSchema.attributesSource
                    : attributes
                }
                renderInput={params =>
                  renderTextField({
                    key: 'select-connection',
                    moreProps: { ...params, placeholder: 'Select fields to source' }
                  })
                }
                style={{ marginBottom: '2%' }}
              />
              {isSQLQuery &&
                renderTextField({
                  key: 'sql-query-text',
                  style: { marginBottom: '2%' },
                  moreProps: {
                    error: alertSQLQuery.error,
                    helperText: alertSQLQuery.error ? alertSQLQuery.text : '',
                    multiline: true,
                    minRows: 2,
                    maxRows: 20,
                    value: textFilterSQL.text,
                    onChange: event => {
                      setTextFilterSQL({ ...textFilterSQL, text: event.target.value });
                      if (viewTable) setViewTable(false);
                    },
                    onBlur: () => handleSQLQueryText(textFilterSQL)
                  }
                })}
            </>
          )}
          <Stack direction="row" display="flex" justifyContent="space-between">
            {showWrapperOptions &&
              !existingWrapper &&
              renderSwitch({
                key: 'generateWrapper',
                checked: generateWrapper,
                onChange: () => setGenerateWrapper(!generateWrapper),
                labelCheck: 'Save as PostgreSQL Layer',
                labelUncheck: 'Save as PostgreSQL Layer'
              })}
            {isPreviewSource && (
              <>
                {renderTextField({
                  key: 'limit-searchig-data',
                  label: 'Limit searching data',
                  style: { width: '30%' },
                  moreProps: {
                    value: String(limitConsultingData),
                    onChange: event => setLimitConsultingData(Number(event.target.value.trim()))
                  }
                })}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={limitConsultingData === 'All'}
                      onChange={
                        limitConsultingData === 'All'
                          ? () => setLimitConsultingData(LIMIT_DATA_DEFAULT)
                          : () => setLimitConsultingData('All')
                      }
                    />
                  }
                  label="All datas"
                />
              </>
            )}
          </Stack>
          {showWrapperOptions &&
            existingWrapper &&
            renderSwitch({
              key: 'overwriteWrapper',
              checked: WrapperValue,
              onChange: () => setWrapperValue(!WrapperValue),
              labelCheck: 'Existing PostgreSQL Layer',
              labelUncheck: 'Existing PostgreSQL Layer'
            })}
          <Stack direction="row" display="flex" justifyContent="flex-start">
            {renderManyButton(buttons)}
          </Stack>
        </Stack>
        <Stack direction="column" width="65%" marginTop="2%">
          {viewTable && (
            <TableSourceComponent
              isExternalSources
              startConnector={newSourceSchema.connector}
              externalSource={newSourceSchema.sourceSelected}
              externalAttributes={newSourceSchema.attributesSource}
              sqlQuery={isSQLQuery ? textFilterSQL.text : ''}
              limitData={limitConsultingData}
            />
          )}
        </Stack>
      </Stack>
    </>
  );
};

NewSource.defaultProps = {
  connectors: [],
  source: {},
  isEditorMode: false
};

NewSource.propTypes = {
  history: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  connectors: PropTypes.array,
  source: PropTypes.object,
  isEditorMode: PropTypes.bool
};

export default withTracker(({ match }) => {
  const sourceId = !isEmpty(match.params) ? match.params.source_id : '';
  const sourceSub = Meteor.subscribe('sources');
  const connectorsSub = Meteor.subscribe('connectors');
  return {
    loading: !sourceSub.ready() || !connectorsSub.ready(),
    source: Sources.findOne(sourceId),
    connectors: Connectors.find({ deleted: { $eq: 'no' } }).fetch()
  };
})(NewSource);
